import logger from '../logger';
import { useCallback } from 'react';
import { useCreateBooking, useGetOutletType } from '../../APICalling/useApiQueries';
import { useAppointment } from '../../Context/Appointment/AppointmentContext';
import { useLocation } from '../../Context/Location/LocationContext';
import { useSchedule } from '../../Context/Schedule/ScheduleContext';
import { useBookingSession } from '../../Context/BookingSession/BookingSessionContext';
import { useGuests } from '../../Context/Guests/GuestsContext';
import { useCustomer } from '../../Context/Customer/CustomerContext';
import { buildMemberServiceData } from './useMemberServiceData';
import { useCartDisplayTotal } from './useCartDisplayTotal';
import { getSessionStorageValue, STORAGE_KEYS } from '../SessionStorage';
import { resolveBookingDateTime } from '../resolveBookingDateTime';

export const getBookingErrorMessage = (payload) => {
  if (payload && typeof payload === "object") {
    if (typeof payload.message === "string" && payload.message.trim())
      return payload.message.trim();
    const inner = payload.data;
    if (
      inner &&
      typeof inner === "object" &&
      !Array.isArray(inner) &&
      typeof inner.message === "string" &&
      inner.message.trim()
    )
      return inner.message.trim();
    if (payload.code === "ERR_NETWORK" || payload.code === "ECONNABORTED") {
      return "Network error. Please check your connection and try again.";
    }
  }
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return "No internet connection. Please try again when you're back online.";
  }
  return "Unable to complete this step. Please try again.";
};

export const buildCreateBookingPayload = ({
  selectedOutletID,
  outletTypeID,
  appointmentTypeID,
  items,
  cartDisplayTotal,
  selectedDate,
  selectedTime,
  occupiedTime,
  customer,
  bookingId,
  action,
  web_notes,
  mobile_number,
  actualMembership,
  uuid,
}) => {
  logger.log("buildCreateBookingPayload", {
    selectedOutletID,
    outletTypeID,
    appointmentTypeID,
    items,
    cartDisplayTotal,
    selectedDate,
    selectedTime,
    occupiedTime,
    customer,
    bookingId,
    action,
    web_notes,
    mobile_number,
    actualMembership,
    uuid,
  });
  const appointment = resolveBookingDateTime(selectedDate, selectedTime);
  const payload = {
    company: 1,
    outlet: selectedOutletID,
    outlet_type: outletTypeID,
    booking_type: appointmentTypeID,
    items,
    sub_total: cartDisplayTotal?.toFixed(2),
    grand_total: cartDisplayTotal?.toFixed(2),
    currency: "SGD",
    membership: actualMembership === true || actualMembership === 1 || actualMembership === '1' ? 1 : 0,
    source: "website",
    payment_type: "payatstore",
    payment_status: 1,
    service_at: `${selectedDate} ${appointment.time}`,
    total_service_duration: occupiedTime,
    panel: "customer",
    // customer,
    mobile_number: mobile_number ?? "",
    ...(uuid && { uuid }),
    web_notes: web_notes ?? "",
    ...(uuid && { type: "update" }),
  };

  if (action === "update") {
    payload.booking_id = bookingId ?? "";
    payload.uuid = uuid ?? "";
    payload.action = "update";
  }

  return payload;
};

/**
 * Shared create/update booking request for Contact Detail and Review pages.
 * Call `createBooking({ userId })` to create, or
 * `createBooking({ userId, action: "update", bookingId })` to update.
 */
export const useCreateBookingRequest = (options = {}) => {
  const { appointmentTypeID, actualMembership } = useAppointment();
  const { selectedOutletID, outletTypeID } = useLocation();
  const { selectedDate, selectedTime, isPublicHoliday } = useSchedule();
  const { bookingID, uuid, occupiedTime } = useBookingSession();
  const { members } = useGuests();
  const { userID, formData } = useCustomer();
  const cartDisplayTotal = useCartDisplayTotal(members, true);
  const createBookingMutation = useCreateBooking(options);
  const { data: outletTypeData } = useGetOutletType({ pagination: 0, status: 1 });
  const outletTypes = outletTypeData?.data?.data?.list?.outlet_type;
  const outletTypeName = Array.isArray(outletTypes)
    ? outletTypes.find((type) => String(type.id) === String(outletTypeID))?.name
    : undefined;

  const createBooking = useCallback(
    (params = {}) => {
      const customer = params.userId ?? userID;
      const action = params.action;
      const bookingId = params.bookingId ?? bookingID ?? "";
      const bookingMembership = params.actualMembership ?? actualMembership;
      const mobileNumber = params.mobile_number ?? formData?.mobile ?? "";
      const customerFirstName = params.customerFirstName ?? formData?.fname;
      const bookingIsPublicHoliday = params.isPublicHoliday ?? isPublicHoliday;

      logger.log("useCreateBookingRequest createBooking",sessionStorage.getItem("uuid"), getSessionStorageValue(STORAGE_KEYS.BOOKING_UUID), uuid, {
        selectedOutletID,
        outletTypeID,
        appointmentTypeID,
        items: JSON.stringify(buildMemberServiceData(members,
          bookingMembership, outletTypeID, selectedDate, selectedTime, outletTypeName, customerFirstName, bookingIsPublicHoliday)),
        actualMembership: bookingMembership,
        cartDisplayTotal,
        selectedDate,
        selectedTime,
        occupiedTime,
        customer,
        bookingId,
        action,
        web_notes: params.web_notes ?? formData?.notes ?? "",
        mobile_number: mobileNumber,
        uuid: getSessionStorageValue(STORAGE_KEYS.BOOKING_UUID) || uuid,
      })
      const payload = buildCreateBookingPayload({
        selectedOutletID,
        outletTypeID,
        appointmentTypeID,
        items: JSON.stringify(buildMemberServiceData(members,
          bookingMembership, outletTypeID, selectedDate, selectedTime, outletTypeName, customerFirstName, bookingIsPublicHoliday)),
        actualMembership: bookingMembership,
        cartDisplayTotal,
        selectedDate,
        selectedTime,
        occupiedTime,
        customer,
        bookingId,
        action,
        web_notes: params.web_notes ?? formData?.notes ?? "",
        mobile_number: mobileNumber,
        uuid: getSessionStorageValue(STORAGE_KEYS.BOOKING_UUID) || uuid,
      });

      createBookingMutation.mutate(payload);
    },
    [
      appointmentTypeID,
      bookingID,
      uuid,
      cartDisplayTotal,
      createBookingMutation,
      formData,
      members,
      actualMembership,
      occupiedTime,
      outletTypeID,
      outletTypeName,
      selectedDate,
      selectedOutletID,
      selectedTime,
      isPublicHoliday,
      userID,
    ]
  );

  return {
    createBooking,
    isPending: createBookingMutation.isPending,
  };
};
